// src/fleet-stats/analytics/mostChanged.js
import { addDays, detectOperatesWeekends, detectSiteOperatesWeekends, smartWeekdayFilter } from "../utils/date.js";
import { BASELINE_WINDOW_DAYS, RECENT_WINDOW_DAYS } from "../constants.js";

export function buildMostChanged(rows, maxDate) {
    if (!maxDate || rows.length === 0) {
        return { biggestDrops: [], biggestGains: [], highUptimeLowDrive: [], becameInactive: [] };
    }

    const recentFrom   = addDays(maxDate, -(RECENT_WINDOW_DAYS - 1));
    const baselineFrom = addDays(maxDate, -(BASELINE_WINDOW_DAYS + RECENT_WINDOW_DAYS - 1));
    const baselineTo   = addDays(maxDate, -RECENT_WINDOW_DAYS);

    // ── Site-level weekend patterns ──────────────────────────────────────────
    const bySiteAll = new Map();
    for (const r of rows) {
        if (!bySiteAll.has(r.siteName)) bySiteAll.set(r.siteName, []);
        bySiteAll.get(r.siteName).push(r);
    }
    const siteWeekendPattern = new Map();
    for (const [siteName, siteRows] of bySiteAll.entries()) {
        siteWeekendPattern.set(siteName, detectSiteOperatesWeekends(siteRows));
    }

    // ── Group by robot ───────────────────────────────────────────────────────
    const byRobot = new Map();
    for (const r of rows) {
        if (!byRobot.has(r.robotId)) {
            byRobot.set(r.robotId, {
                robotId: r.robotId, robotName: r.robotName,
                client: r.client, siteName: r.siteName,
                allDays: [], recent: [], baseline: [],
            });
        }
        const e = byRobot.get(r.robotId);
        e.allDays.push(r);
        if (r.date >= recentFrom)                           e.recent.push(r);
        if (r.date >= baselineFrom && r.date <= baselineTo) e.baseline.push(r);
    }

    const avg = (arr, fn) =>
        arr.length === 0 ? 0 : arr.reduce((s, r) => s + fn(r), 0) / arr.length;

    const drops = [], gains = [], highUptimeLow = [], becameInactive = [];

    for (const e of byRobot.values()) {
        if (e.baseline.length < 3) continue;

        const robotWknd = detectOperatesWeekends(e.allDays);
        const siteWknd  = siteWeekendPattern.get(e.siteName) ?? null;

        const bFiltered = smartWeekdayFilter(e.baseline, robotWknd, siteWknd);
        const rFiltered = smartWeekdayFilter(e.recent,   robotWknd, siteWknd);

        const bKm     = avg(bFiltered, (r) => r.distance_m / 1000);
        const rKm     = avg(rFiltered, (r) => r.distance_m / 1000);
        const bHours  = avg(bFiltered, (r) => r.moving_time_s / 3600);
        const rHours  = avg(rFiltered, (r) => r.moving_time_s / 3600);

        if (bKm < 0.05) continue;

        const changePct = (rKm - bKm) / bKm;
        const entry = {
            robotId: e.robotId, robotName: e.robotName,
            client: e.client, siteName: e.siteName,
            recentKm: rKm, baselineKm: bKm,
            recentHours: rHours, baselineHours: bHours,
            changePct,
        };

        if (changePct < -0.4) drops.push(entry);
        if (changePct > 0.5)  gains.push(entry);

        // High uptime, low movement — not weekend-filtered (uptime is continuous)
        const recentUptime  = e.recent.reduce((s, r) => s + (r.uptime_s || 0), 0) / 3600;
        const recentDriving = e.recent.reduce((s, r) => s + r.moving_time_s, 0) / 3600;
        const util = recentUptime > 0 ? recentDriving / recentUptime : null;
        if (util !== null && util < 0.1 && recentUptime > 5) {
            highUptimeLow.push({ ...entry, recentUptimeH: recentUptime, recentDrivingH: recentDriving, utilization: util });
        }

        // Became inactive
        if (bKm > 0.05 && rKm === 0) becameInactive.push(entry);
    }

    drops.sort((a, b) => a.changePct - b.changePct);
    gains.sort((a, b) => b.changePct - a.changePct);
    highUptimeLow.sort((a, b) => a.utilization - b.utilization);
    becameInactive.sort((a, b) => b.baselineKm - a.baselineKm);

    // Full lists — display caps belong to the UI (becameInactive is the
    // canonical "went quiet" list for the inactive drill-down)
    return {
        biggestDrops:     drops,
        biggestGains:     gains,
        highUptimeLowDrive: highUptimeLow,
        becameInactive,
    };
}